import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("dara_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then(r => {
        setUser(r.data);
        // Phase 25.13m — cache super-admin flag so usePageview can stamp
        // own-team QA traffic as internal without an extra round-trip.
        if (r.data?.is_super_admin) {
          localStorage.setItem("dara_is_super_admin", "1");
        } else {
          localStorage.removeItem("dara_is_super_admin");
        }
      })
      .catch((err) => {
        // Phase 25.13w — Only clear the session on a real 401 Unauthorized.
        // Network blips, 502s during Cloudflare cold-cache, or transient
        // 503s during OAuth redirect chains should NEVER blow away a valid
        // 7-day session. This was the silent bug behind Square OAuth users
        // getting bounced to /login mid-reconnect.
        if (err?.response?.status === 401) {
          localStorage.removeItem("dara_token");
          localStorage.removeItem("dara_is_super_admin");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("dara_token", data.token);
    if (data.user?.is_super_admin) {
      localStorage.setItem("dara_is_super_admin", "1");
    } else {
      localStorage.removeItem("dara_is_super_admin");
    }
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("dara_token", data.token);
    if (data.user?.is_super_admin) {
      localStorage.setItem("dara_is_super_admin", "1");
    } else {
      localStorage.removeItem("dara_is_super_admin");
    }
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("dara_token");
    localStorage.removeItem("dara_is_super_admin");
    setUser(null);
  };

  const setSession = (token, userObj) => {
    localStorage.setItem("dara_token", token);
    setUser(userObj);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, logout, setSession }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
